/**
 * useMission Hook
 *
 * High-level mission operations hook
 * Provides convenient access to mission store and operations
 */

import { useMissionStore } from '@/stores/useMissionStore';
import type { Mission, VehicleType, MissionStatus } from '../types/mission.types';
import type { Waypoint, WaypointInput } from '../types/waypoint.types';

/**
 * Hook return type
 */
interface UseMissionReturn {
  // Current Mission
  mission: Mission | null;
  waypoints: Waypoint[];

  // Mission Operations
  createMission: (name: string, vehicleType?: VehicleType) => void;
  loadMission: (missionId: string) => void;
  saveMission: () => void;
  deleteMission: (missionId: string) => void;
  updateMissionStatus: (status: MissionStatus) => void;
  updateMissionName: (name: string) => void;

  // Waypoint Operations
  addWaypoint: (input: WaypointInput) => void;
  updateWaypoint: (waypointId: string, updates: Partial<Waypoint>) => void;
  deleteWaypoint: (waypointId: string) => void;
  clearAllWaypoints: () => void;

  // Selection
  selectedWaypointId: string | null;
  selectWaypoint: (waypointId: string | null) => void;

  // History
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;

  // UI State
  isAddingWaypoint: boolean;
  setAddingWaypointMode: (isAdding: boolean) => void;
}

/**
 * High-level mission operations hook
 */
export function useMission(): UseMissionReturn {
  const mission = useMissionStore((state) => state.currentMission);
  const waypoints = mission?.waypoints ?? [];
  const selectedWaypointId = useMissionStore((state) => state.selectedWaypointId);
  const isAddingWaypoint = useMissionStore((state) => state.isAddingWaypoint);

  const createMission = useMissionStore((state) => state.createMission);
  const loadMission = useMissionStore((state) => state.loadMission);
  const saveMission = useMissionStore((state) => state.saveMission);
  const deleteMission = useMissionStore((state) => state.deleteMission);
  const updateMissionStatus = useMissionStore((state) => state.updateMissionStatus);
  const updateMissionName = useMissionStore((state) => state.updateMissionName);

  const addWaypoint = useMissionStore((state) => state.addWaypoint);
  const updateWaypoint = useMissionStore((state) => state.updateWaypoint);
  const deleteWaypoint = useMissionStore((state) => state.deleteWaypoint);
  const clearAllWaypoints = useMissionStore((state) => state.clearAllWaypoints);

  const selectWaypoint = useMissionStore((state) => state.selectWaypoint);
  const setAddingWaypointMode = useMissionStore((state) => state.setAddingWaypointMode);

  const undo = useMissionStore((state) => state.undo);
  const redo = useMissionStore((state) => state.redo);
  const canUndo = useMissionStore((state) => state.canUndo());
  const canRedo = useMissionStore((state) => state.canRedo());

  return {
    mission,
    waypoints,
    createMission,
    loadMission,
    saveMission,
    deleteMission,
    updateMissionStatus,
    updateMissionName,
    addWaypoint,
    updateWaypoint,
    deleteWaypoint,
    clearAllWaypoints,
    selectedWaypointId,
    selectWaypoint,
    undo,
    redo,
    canUndo,
    canRedo,
    isAddingWaypoint,
    setAddingWaypointMode,
  };
}
